import { ArrowUpRight } from 'lucide-react';
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';

import { InAppShellContext } from '../layout/InAppShellContext';
import theme from './theme.module.css';

/**
 * The chrome every public page wears: KN's top bar and footer (SRS #56).
 *
 * KN approved the "/home2" landing page and asked for news, pricing and the
 * FAQ to follow it. Those pages had three different headers between them — a
 * newspaper masthead, the application's own bar, and none at all — so a
 * visitor moving between them was visibly leaving one site and arriving at
 * another. One shell means that cannot happen again, and that adding a public
 * page gets the brand for free rather than by copying markup.
 *
 * The bar carries Log in and Register because they are the one thing no page
 * repeats in its body: a visitor who already has an account otherwise has no
 * way in from a public page.
 */

export interface NavItem {
  label: string;
  /** An in-page anchor ("#features") or a route ("/pricing"). */
  to: string;
}

/** The public site's own pages, shown on every one of them including the
 *  landing page — News and Pricing have to be reachable from the front door,
 *  not only from the pages that are not it.
 *
 *  Home points at "/" rather than "/home2" although both render the same
 *  page: "/" is the canonical URL, and sending someone who is already on it
 *  to a second address for the same content is confusing to them and reads as
 *  duplicate content to a crawler. "/home2" stays reachable because it is the
 *  link KN has been sharing. */
export const SITE_NAV: NavItem[] = [
  { label: 'Home', to: '/' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'FAQ', to: '/faq' },
];

interface Props {
  children: React.ReactNode;
  nav?: NavItem[];
  /** Extra class on the outer element, for a page that adds its own rules. */
  className?: string;
}

export const MarketingShell: React.FC<Props> = ({ children, nav = SITE_NAV, className }) => {
  // Inside the signed-in shell the menu is the navigation and the reader is already signed in, so the
  // public bar (with its Log in and Register) is left out. A visitor sees the bar as before.
  const inAppShell = useContext(InAppShellContext);
  return (
    <div className={`${theme.theme} ${className ?? ''}`.trim()}>
      {!inAppShell && (
        <header className={theme.topBar}>
          <div className={theme.barInner}>
            <Link to="/" className={theme.brand}>
              <img src="/home2-logo.png" alt="PolicyOra" />
            </Link>
            <nav className={theme.nav} aria-label="Primary">
              {nav.map(({ label, to }) => (
                to.startsWith('#')
                  ? <a key={to} className={theme.navLink} href={to}>{label}</a>
                  : <Link key={to} className={theme.navLink} to={to}>{label}</Link>
              ))}
            </nav>
            <div className={theme.authActions}>
              <Link to="/login" className={theme.navAuth}>Log in</Link>
              <Link to="/signup" className={theme.btnPrimary}>
                Register <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </header>
      )}

      <main>{children}</main>

      <footer className={theme.footer}>
        <div className={`${theme.wrap} ${theme.footerInner}`}>
          <img src="/home2-logo.png" alt="PolicyOra" className={theme.footerLogo} />
          <p>PolicyOra is an initiative of Knowledge Networks Inc.</p>
        </div>
      </footer>
    </div>
  );
};

export default MarketingShell;
